/**
 * StateCarbonPathways — /states/carbon-pathways
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStateContext } from "@/hooks/useStateContext";
import { RefreshCw, CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import client from "@/api/client";
import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";

const STATUS_ICONS = {
  platform_supported:        <CheckCircle2 size={14} className="text-emerald-500 shrink-0" />,
  requires_government_action: <AlertTriangle size={14} className="text-amber-500 shrink-0" />,
  indicative_only:            <AlertTriangle size={14} className="text-blue-500 shrink-0" />,
  not_connected:              <XCircle size={14} className="text-red-400 shrink-0" />,
  no_itmos:                   <XCircle size={14} className="text-slate-400 shrink-0" />,
  itmo_transactions_present:  <CheckCircle2 size={14} className="text-teal-500 shrink-0" />,
};

const STATUS_BADGE = {
  platform_supported:        "bg-emerald-100 text-emerald-700",
  requires_government_action: "bg-amber-100 text-amber-700",
};

export default function StateCarbonPathways() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { stateId, geography: geo, stateParams } = useStateContext();

  useEffect(() => {
    client.get(`/states/article6-readiness${stateParams}`)
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [geo]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <RefreshCw size={18} className="animate-spin text-teal-500" />
    </div>
  );

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        label="STATES WORKSPACE"
        title="Carbon Pathways & Article 6"
        subtitle={`${geo} · ${data?.readiness_label}`}
        backUrl="/states/overview"
        backLabel="State Overview"
      />

      <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-3">
        <p className="text-xs text-amber-700">{data?.disclaimer}</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <MetricCard
          label="Platform Projects"
          value={data?.platform_projects || 0}
          sub="DeCarbonIQ registered"
          color="text-teal-700"
        />
        <MetricCard
          label="CAI Projects (ITMO)"
          value={data?.cai_projects_count || 0}
          sub="With ITMO transactions"
          color="text-blue-700"
        />
      </div>

      <SectionCard title="Article 6 Readiness Gates" noPadding>
        {(data?.checks || []).map((check) => (
          <div key={check.gate} className="px-5 py-4 border-b border-slate-50 last:border-0">
            <div className="flex items-start gap-3">
              {STATUS_ICONS[check.status] || STATUS_ICONS.indicative_only}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900">{check.label}</p>
                <p className="text-xs text-slate-500 mt-0.5">{check.description}</p>
                <p className="text-xs text-teal-600 font-medium mt-1">→ {check.action}</p>
              </div>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full shrink-0 flex-shrink-0 ${
                STATUS_BADGE[check.status] || "bg-slate-100 text-slate-500"
              }`}>
                {check.status.replace(/_/g, " ")}
              </span>
            </div>
          </div>
        ))}
      </SectionCard>

      <CTAButton variant="outline" className="w-full justify-center" onClick={() => navigate("/cai/reporting")}>
        View CAI ITMO Workspace →
      </CTAButton>
    </div>
  );
}
